import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      return res.data.data; // LoginResult: { twoFactorRequired, email, maskedPhone, auth }
    } catch (err) {
      return rejectWithValue(err.response?.data?.error ?? "Giriş başarısız");
    }
  }
);

// 2FA açık kullanıcı için SMS ile gelen 6 haneli kodu doğrular.
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/verify-otp", { email, code });
      return res.data.data; // { accessToken, refreshToken, user }
    } catch (err) {
      return rejectWithValue(err.response?.data?.error ?? "Doğrulama başarısız");
    }
  }
);

export const acceptInvitation = createAsyncThunk(
  "auth/acceptInvitation",
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const res = await api.post("/auth/accept-invitation", { token, password });
      return res.data.data; // { accessToken, refreshToken, user }
    } catch (err) {
      return rejectWithValue(err.response?.data?.error ?? "Aktivasyon başarısız");
    }
  }
);

// Güncel kullanıcı bilgisini çek (ör. hasCashPassword değişince tazelemek için).
export const fetchMe = createAsyncThunk(
  "auth/me",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/accounts/me");
      return res.data.data; // UserSummary
    } catch (err) {
      return rejectWithValue(err.response?.data?.error ?? "Profil yüklenemedi");
    }
  }
);

// Profil ayarlarından 2FA'yı aç/kapat (yalnız Gold). Dönen UserSummary ile
// kullanıcıyı tazeler.
export const setTwoFactor = createAsyncThunk(
  "auth/setTwoFactor",
  async (enabled, { rejectWithValue }) => {
    try {
      const res = await api.post("/accounts/two-factor", { enabled });
      return res.data.data; // UserSummary
    } catch (err) {
      return rejectWithValue(err.response?.data?.error ?? "İşlem başarısız");
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      await api.post("/accounts/change-password", { oldPassword, newPassword });
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error ?? "Şifre değiştirilemedi");
    }
  }
);

const storedUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
})();

const initialState = {
  user: storedUser,
  accessToken: localStorage.getItem("accessToken"),
  refreshToken: localStorage.getItem("refreshToken"),
  isLoading: false,
  error: null,
  isAuthenticated: !!localStorage.getItem("accessToken"),
  // 2FA beklemede: { email, maskedPhone } — kod doğrulanana kadar oturum açılmaz.
  pendingTwoFactor: null,
};

// Token+kullanıcıyı state ve localStorage'a yazar (login / 2FA doğrulama ortak).
function applyAuth(state, auth) {
  const { accessToken, refreshToken, user } = auth;
  state.isAuthenticated = true;
  state.accessToken = accessToken;
  state.refreshToken = refreshToken;
  state.user = user;
  state.pendingTwoFactor = null;
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("user", JSON.stringify(user));
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.pendingTwoFactor = null;
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    },
    // 2FA ekranından geri dön (kod girmekten vazgeç).
    cancelTwoFactor: (state) => {
      state.pendingTwoFactor = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload; // LoginResult
        if (data.twoFactorRequired) {
          state.pendingTwoFactor = { email: data.email, maskedPhone: data.maskedPhone };
        } else {
          applyAuth(state, data.auth);
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 2FA kod doğrulama
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        applyAuth(state, action.payload);
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // davet kabulünde otomatik oturum aç (login ile aynı)
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        const { accessToken, refreshToken, user } = action.payload;
        state.isAuthenticated = true;
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        state.user = user;
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(user));
      })
      // /me tazelemesi: kullanıcı özetini güncelle (hasCashPassword vb.)
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem("user", JSON.stringify(state.user));
      })
      // 2FA aç/kapat sonucu: kullanıcıyı tazele
      .addCase(setTwoFactor.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem("user", JSON.stringify(state.user));
      })
      // şifre değişince mustChangePassword bayrağını temizle
      .addCase(changePassword.fulfilled, (state) => {
        if (state.user) {
          state.user = { ...state.user, mustChangePassword: false };
          localStorage.setItem("user", JSON.stringify(state.user));
        }
      });
  },
});

export const { logout, cancelTwoFactor } = authSlice.actions;
export default authSlice.reducer;
